import { Camera, Loader2, Mail, Pencil, Phone, X } from 'lucide-react';
import { formatMobileWithCountryCode } from '@/features/auth/utils/mobileNumber';
import type { ProfileData } from '@/features/profile/api/useProfileQueries';
import type { ProfileOverviewForm } from '../../ProfilePage.types';

type ProfileBannerProps = {
  user?: ProfileData;
  form: ProfileOverviewForm;
  editing: boolean;
  onToggleEdit: () => void;
  photoPreview: string | null;
  photoBusy: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onOpenPhotoPicker: () => void;
  onPhotoFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export function ProfileBanner({
  user,
  form,
  editing,
  onToggleEdit,
  photoPreview,
  photoBusy,
  fileInputRef,
  onOpenPhotoPicker,
  onPhotoFileChange,
}: ProfileBannerProps) {
  const statusLabel = user?.status?.replaceAll('_', ' ') || 'PENDING';
  const isVerified = statusLabel.toUpperCase() === 'VERIFIED';

  return (
    <div className="relative overflow-hidden rounded-2xl">
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #145E8C 0%, #007A78 40%, #33A65B 100%)',
        }}
      />
      <div className="relative z-10 flex flex-col items-center gap-5 px-6 py-7 text-white sm:flex-row sm:gap-6 sm:px-10 sm:py-8">
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={onOpenPhotoPicker}
            disabled={photoBusy}
            aria-label="Update profile photo"
            className="group relative flex h-[88px] w-[88px] items-center justify-center overflow-hidden rounded-full border-[3px] border-white/70 bg-white/20 backdrop-blur-sm transition hover:border-white disabled:cursor-wait"
          >
            {photoPreview ? (
              <img
                src={photoPreview}
                alt=""
                className={`h-full w-full object-cover transition ${photoBusy ? 'opacity-50' : 'group-hover:opacity-80'}`}
              />
            ) : (
              <span className={`text-3xl font-bold opacity-90 ${photoBusy ? 'opacity-50' : ''}`}>
                {(user?.firstName?.[0] || 'U').toUpperCase()}
              </span>
            )}
            {photoBusy ? (
              <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                <Loader2 className="h-5 w-5 animate-spin text-white" />
              </span>
            ) : (
              <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition group-hover:opacity-100">
                <Camera className="h-5 w-5 text-white" />
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={onOpenPhotoPicker}
            disabled={photoBusy}
            aria-label="Change photo"
            className="absolute -right-0.5 -bottom-0.5 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-[var(--dash-brand)] text-white shadow-md transition hover:brightness-110 disabled:pointer-events-none disabled:opacity-60"
          >
            <Camera className="h-3.5 w-3.5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={onPhotoFileChange}
          />
        </div>

        <div className="flex flex-1 flex-col items-center gap-1.5 text-center sm:items-start sm:text-left">
          <h2 className="m-0 text-[22px] font-bold leading-tight">
            {user?.firstName} {user?.lastName}
          </h2>
          <p className="m-0 text-[12px] text-white/80 sm:hidden">
            Tap photo to update
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-white/25 bg-white/10 px-3 py-[3px] text-[10px] font-bold tracking-widest uppercase backdrop-blur-sm">
              {user?.role?.replaceAll('_', ' ') || 'Advertiser'}
            </span>
            <span
              className={`rounded-full px-2.5 py-[3px] text-[10px] font-bold uppercase ${
                isVerified
                  ? 'bg-emerald-400/20 text-emerald-100'
                  : 'bg-amber-400/20 text-amber-100'
              }`}
            >
              {statusLabel}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-4 text-[13px] opacity-90">
            <span className="flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 opacity-70" />
              {user?.email}
            </span>
            {form.mobileNumber ? (
              <span className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 opacity-70" />
                {formatMobileWithCountryCode(form.mobileNumber, form.countryCode)}
              </span>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          onClick={onToggleEdit}
          className={`flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2.5 text-xs font-bold tracking-wide uppercase shadow-sm transition ${
            editing
              ? 'bg-white/90 text-red-600 hover:bg-white'
              : 'bg-white/90 text-[var(--dash-brand)] hover:bg-white'
          }`}
        >
          {editing ? (
            <>
              <X className="h-3.5 w-3.5" /> Cancel
            </>
          ) : (
            <>
              <Pencil className="h-3.5 w-3.5" /> Edit Profile
            </>
          )}
        </button>
      </div>
    </div>
  );
}
